import { useEffect, useState, type ReactNode } from 'react';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';
import { Button } from '@components/common/Button';
import { Icon } from '@components/Icon';
import { organizationDocumentsApi, openOrganizationDocument } from '@services/organizationDocumentsApi';
import type { PublicOrganizationDocument } from '@lambda/shared/types';

const SECTION_DESCRIPTION =
  'Access important documents, schedules, and policies for BHG families.';

const hasApi = Boolean((import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''));

export function ResourcesSection() {
  const [landingDocs, setLandingDocs] = useState<PublicOrganizationDocument[]>([]);
  const [loading, setLoading] = useState(hasApi);

  useEffect(() => {
    if (!hasApi) return;
    let cancelled = false;
    organizationDocumentsApi
      .listPublic()
      .then((res) => {
        if (cancelled) return;
        const docs = (res.data?.documents ?? [])
          .filter((d) => d.showOnLanding && d.hasFile)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        setLandingDocs(docs);
      })
      .catch(() => {
        if (!cancelled) setLandingDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const docCards = landingDocs.map((doc) => ({
    icon: 'file-text' as const,
    title: doc.title,
    description: doc.description ?? 'Review this document.',
    buttonLabel: 'View PDF',
    onClick: () => openOrganizationDocument(doc.documentId),
  }));

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="bhg-landing-resources__grid" aria-busy="true" aria-label="Loading resources">
        {[0, 1, 2].map((i) => (
          <Card key={i} elevation={2} className="bhg-landing-resource-card bhg-landing-resource-card--skeleton">
            <div className="bhg-landing-resource-card__header">
              <div className="bhg-landing-resource-card__skeleton-icon" aria-hidden />
              <div className="bhg-landing-resource-card__skeleton-title" aria-hidden />
            </div>
            <div className="bhg-landing-resource-card__skeleton-line" aria-hidden />
            <div className="bhg-landing-resource-card__skeleton-line bhg-landing-resource-card__skeleton-line--short" aria-hidden />
            <div className="bhg-landing-resource-card__skeleton-button" aria-hidden />
          </Card>
        ))}
      </div>
    );
  } else if (docCards.length > 0) {
    body = (
      <div className="bhg-landing-resources__grid">
        {docCards.map((card) => (
          <Card
            key={card.title}
            elevation={2}
            interactive
            className="bhg-landing-resource-card"
          >
            <div className="bhg-landing-resource-card__header">
              <div className="bhg-landing-resource-card__icon">
                <Icon
                  type={card.icon}
                  style="filled"
                  colorVariant="blue"
                  size="lg"
                  aria-label=""
                />
              </div>
              <h3 className="bhg-landing-resource-card__title">{card.title}</h3>
            </div>
            <p className="bhg-landing-resource-card__description">{card.description}</p>
            <Button variant="outline" size="sm" onClick={card.onClick}>
              {card.buttonLabel}
            </Button>
          </Card>
        ))}
      </div>
    );
  } else {
    body = (
      <div className="bhg-landing-resources__empty">
        <Card elevation={2} className="bhg-landing-resources__empty-card">
          <Icon
            type="file-text"
            style="outlined"
            colorVariant="blue"
            size="xl"
            aria-label=""
          />
          <p role="status">Documents will be posted here when available.</p>
        </Card>
      </div>
    );
  }

  return (
    <section id="resources" className="bhg-landing-section bhg-landing-resources">
      <Container size="lg">
        <div className="bhg-landing-resources__header">
          <h2 id="resources-heading" className="bhg-landing-section__title bhg-landing-resources__title">
            Resources and Documents
          </h2>
          <p className="bhg-landing-resources__description">{SECTION_DESCRIPTION}</p>
        </div>

        {body}
      </Container>
    </section>
  );
}
